<template>
  <div class="relative" ref="dropdownRef">
    <button
      v-if="!isOpen"
      ref="triggerButton"
      @click="openDropdown"
      class="filter-dropdown"
    >
      <span v-if="modelValue !== allLabel">{{ modelValue }}</span>
      <slot v-else name="placeholder">
        {{ allLabel }}
      </slot>
      <ChevronDown class="w-4 h-4 flex-shrink-0" />
    </button>

    <div
      v-else
      class="filter-dropdown filter-dropdown--active"
      :style="{ width: lockedWidth }"
    >
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="modelValue !== allLabel ? modelValue : allLabel"
        class="searchable-dropdown-input"
        @keydown.escape="closeDropdown"
      />
      <ChevronDown class="w-4 h-4 flex-shrink-0 rotate-180" />
    </div>

    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
    >
      <ul class="max-h-60 overflow-auto text-sm">
        <li
          v-for="option in filteredOptions"
          :key="option"
          @click="selectOption(option)"
          class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
          :class="option === modelValue ? 'searchable-dropdown-selected' : ''"
        >
          {{ option }}
          <svg
            v-if="option === modelValue"
            class="w-4 h-4 flex-shrink-0 text-brand-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M16.667 5L7.5 14.167L3.333 10" />
          </svg>
        </li>
        <li
          v-if="filteredOptions.length === 0"
          class="px-4 py-2 text-gray-400 italic"
        >
          No results found
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import ChevronDown from "./icons/ChevronDown.vue";

const props = defineProps({
  modelValue: {
    type: [String, Number],
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  allLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue"]);

const dropdownRef = ref(null);
const triggerButton = ref(null);
const searchInput = ref(null);
const isOpen = ref(false);
const searchQuery = ref("");
const lockedWidth = ref(null);

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;
  const query = searchQuery.value.toLowerCase();
  return props.options.filter(
    (option) =>
      option === props.allLabel || String(option).toLowerCase().includes(query)
  );
});

const openDropdown = () => {
  lockedWidth.value = triggerButton.value?.offsetWidth + "px";
  isOpen.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = "";
  lockedWidth.value = null;
};

const selectOption = (option) => {
  emit("update:modelValue", option);
  closeDropdown();
};

const handleClickOutside = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    closeDropdown();
  }
};

onMounted(() => document.addEventListener("mousedown", handleClickOutside));
onUnmounted(() => document.removeEventListener("mousedown", handleClickOutside));
</script>

<style scoped>
.filter-dropdown--active {
  border-color: #265957;
  box-shadow: 0 0 0 1px #265957;
}

.searchable-dropdown-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: inherit;
  font-family: inherit;
  font-weight: inherit;
  color: inherit;
  width: 100%;
  padding: 0;
  min-width: 0;
}

.searchable-dropdown-input::placeholder {
  color: #9ca3af;
}

.searchable-dropdown-selected {
  background-color: #EAF7F6;
}
</style>
